import { Button, InputAdornment, MenuItem, Stack, SvgIcon, TextField } from '@mui/material';
import { Box } from '@mui/system';
import { get, map, upperCase } from 'lodash';
import { ChangeEvent, ComponentType, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { QUESTION_LANGUAGES } from 'src/common/lang-constants';
import { Icons } from 'src/components/icons';
import { useAuth } from '../../../hooks';
import { DirectionsSelect } from '../../directions';
import { OrganizationsSelect } from '../../organizations';

const ORDER = {
  DATE_ASC: 'id',
  DATE_DESC: '-id',
};

const ORDER_VARIANTS = [
  {
    value: ORDER.DATE_ASC,
    label: 'Сначала новые',
    labelKey: 'order.dateAsc',
  },
  {
    value: ORDER.DATE_DESC,
    label: 'Сначала старые',
    labelKey: 'order.dateDesc',
  },
];

interface TestsPanelProps {
  query: Record<string, any>;
  onUpdate: (x: Record<string, any>) => void;
}
export const TestsPanel: ComponentType<TestsPanelProps> = ({ onUpdate }) => {
  const { t } = useTranslation();
  const { isSuperAdmin, user, isOrganizationAdmin } = useAuth();

  const [search, setSearch] = useState('');
  const [order, setOrder] = useState(ORDER.DATE_ASC);
  const [organization, setOrganization] = useState('');
  const [direction, setDirection] = useState('');
  const [language, setLanguage] = useState('');

  useEffect(() => {
    if (isOrganizationAdmin) {
      setOrganization(get(user, ['data', 'organization', 'id'], ''));
    }
  }, [user, isOrganizationAdmin]);

  const handleSubmit = () => {
    const newParamsObject = {
      search,
      order,
      organization,
      direction,
      language,
      page: 1,
    };
    onUpdate(newParamsObject);
  };

  return (
    <Box>
      {/* top */}
      <Stack direction="row" justifyContent="space-between" alignItems="flex-end" mb={3}>
        {/* top - search */}
        <Stack direction="row" alignItems="flex-end" spacing={3}>
          <TextField
            label={t('tests.search', 'Поиск по тестам')}
            placeholder={t('search', 'Поиск')}
            value={search}
            size="small"
            sx={{ width: 300 }}
            onChange={(e: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) =>
              setSearch(e.target.value)
            }
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SvgIcon component={Icons.Search} viewBox="0 0 24 24" color="primary" />
                </InputAdornment>
              ),
            }}
          />

          {isSuperAdmin && (
            <Box>
              <OrganizationsSelect
                prompt={'' + t('all', 'Все')}
                onChange={(e: any) => setOrganization(e.target.value)}
                label={t('profile.organization', 'Организация')}
                placeholder={t('profile.organization.placeholder', 'Выберите организацию')}
                size="small"
                value={organization}
                sx={{ width: 200 }}
              />
            </Box>
          )}

          <Box>
            <DirectionsSelect
              organization={organization}
              prompt={'' + t('all', 'Все')}
              onChange={(e: any) => setDirection(e.target.value)}
              label={t('profile.directions', 'Направление')}
              placeholder={t('profile.directions.placeholder', 'Выберите Направление')}
              size="small"
              value={direction}
              sx={{ width: 200 }}
            />
          </Box>

          <TextField
            label={t('language', 'Язык')}
            placeholder="Выберите язык"
            value={language}
            size="small"
            sx={{ width: 100 }}
            onChange={(e: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) =>
              setLanguage(e.target.value)
            }
            select
          >
            {map(QUESTION_LANGUAGES, (item) => (
              <MenuItem key={item.code} value={item.code}>
                {upperCase(item.code)}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            label={t('sort', 'Сортировка')}
            placeholder="Поиск"
            value={order}
            size="small"
            sx={{ width: 200 }}
            onChange={(e: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) =>
              setOrder(e.target.value)
            }
            select
          >
            {map(ORDER_VARIANTS, (variant, index) => (
              <MenuItem key={index} value={variant.value}>
                {t(variant.labelKey, variant.label)}
              </MenuItem>
            ))}
          </TextField>

          <Button variant="contained" color="secondary" type="submit" onClick={handleSubmit}>
            {t('action.search', 'Найти')}
          </Button>
        </Stack>
      </Stack>
    </Box>
  );
};
